import setAppPaneUnderlayVisibility from 'owa-app-pane-underlay/lib/actions/setAppPaneUnderlayVisibility';
import { drawerPaneClosed } from '../actions/drawerPaneClosed';
import removeActiveDrawerPane from '../mutators/removeActiveDrawerPane';
import getActiveDrawerPane from '../selectors/getActiveDrawerPane';
import getTabIdFromWindow from '../utils/getTabIdFromWindow';
import getUnderlay from '../utils/getUnderlay';

/**
 * Closes the specified drawer pane for the given target window.
 * @param paneId - The ID of the pane to close.
 * @param targetWindow - The target window where the pane should be closed.
 * @param dispatchCloseAction - Whether to dispatch the close action.
 */
export function closeDrawerPane(
    paneId: string,
    targetWindow: Window = window,
    dispatchCloseAction: boolean = true
): boolean {
    const tabId = getTabIdFromWindow(targetWindow);
    const activePane = getActiveDrawerPane(tabId);

    // If the pane is not open, we don't need to do anything.
    if (activePane !== paneId) {
        return false;
    }

    closeDrawerPaneInternal(paneId, tabId, targetWindow, dispatchCloseAction);
    return true;
}

/**
 * Closes any active drawer pane for the given target window.
 * @param targetWindow - The target window where the pane should be closed.
 * @param dispatchCloseAction - Whether to dispatch the close action.
 */
export function closeAnyActiveDrawerPane(
    targetWindow: Window = window,
    dispatchCloseAction: boolean = true
): boolean {
    const tabId = getTabIdFromWindow(targetWindow);
    const activePane = getActiveDrawerPane(tabId);

    if (!activePane) {
        return false;
    }

    closeDrawerPaneInternal(activePane, tabId, targetWindow, dispatchCloseAction);
    return true;
}

/**
 * Internal function to close the active drawer pane.
 * Assumes the caller has already validated the pane should be closed.
 */
function closeDrawerPaneInternal(
    paneId: string,
    tabId: string,
    targetWindow: Window,
    dispatchCloseAction: boolean
): void {
    // Remove the pane from the active panes.
    removeActiveDrawerPane(tabId);

    // Hide the underlay for the drawer pane.
    setAppPaneUnderlayVisibility(
        'drawerPane',
        false /* isShown */,
        true /* shrinkable */,
        undefined /* isPlaceholder */,
        getUnderlay(targetWindow) /* underlay */
    );

    if (dispatchCloseAction) {
        // Notify that the drawer pane has been closed.
        drawerPaneClosed(paneId, targetWindow);
    }
}
